import { settingOrDefault, type GameType, type SettingKey } from '@puzzlio/core';

export class SettingsAPI {
  private static readonly STORAGE_KEY = 'puzzlio_settings';

  /**
   * Save a global setting to local storage
   */
  static saveSetting(key: string, value: any): void {
    const settings = this.getAllSettings();
    settings[key] = value;
    this.saveAllSettings(settings);
  }

  /**
   * Get a global setting from local storage, or its default from SettingsInfo
   */
  static getSetting(key: SettingKey): any {
    return settingOrDefault(key, this.getAllSettings()[key]);
  }

  /**
   * Save a game-specific setting to local storage
   */
  static saveGameSetting(gameType: GameType, key: string, value: any): void {
    const settings = this.getAllSettings();
    if (!settings.games) {
      settings.games = {};
    }
    if (!settings.games[gameType]) {
      settings.games[gameType] = {};
    }
    settings.games[gameType][key] = value;
    this.saveAllSettings(settings);
  }

  /**
   * Get a game-specific setting from local storage, or its default from SettingsInfo
   */
  static getGameSetting(gameType: GameType, key: SettingKey): any {
    return settingOrDefault(key, this.getAllSettings().games?.[gameType]?.[key]);
  }

  /**
   * Get all settings for a specific game
   */
  static getGameSettings(gameType: GameType): Record<string, any> {
    const settings = this.getAllSettings();
    return settings.games?.[gameType] || {};
  }

  /**
   * Get all settings from local storage
   */
  static getAllSettings(): Record<string, any> {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading settings from localStorage:', error);
      return {};
    }
  }

  /**
   * Save all settings to local storage. Public because the React provider writes the whole blob,
   * having already computed the next state.
   */
  static saveAllSettings(settings: Record<string, any>): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
      // Dispatch custom event for same-tab updates
      window.dispatchEvent(new CustomEvent('settingsChanged'));
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  }

  /**
   * Clear all settings
   */
  static clearAllSettings(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing settings from localStorage:', error);
    }
  }

  /**
   * Clear settings for a specific game
   */
  static clearGameSettings(gameType: GameType): void {
    const settings = this.getAllSettings();
    if (settings.games?.[gameType]) {
      delete settings.games[gameType];
      this.saveAllSettings(settings);
    }
  }
}
